import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import emailjs from "@emailjs/browser";
import SiteFooter from "../../general/components/layout/SiteFooter";
import SiteNavbar from "../../general/components/layout/SiteNavbar";
import mercadoPagoLogo from "../assets/media/img/mp-celeste.png";
import compraSeguraBadge from "../assets/media/img/compra-segura.png";
import { routes } from "../../general/routes/newsApi";
import {
    getCoursePath,
    getEnrollmentSuccessPath,
    resolveCourse,
} from "../components/courseRouteUtils";

const DLOCAL_SMART_FIELDS_SRC =
    "https://checkout.dlocalgo.com/js/dlocalgo-smartfields-bundled.js";
const DLOCAL_SMART_FIELDS_API_KEY =
    process.env.REACT_APP_DLOCAL_SMARTFIELDS_API_KEY ||
    "653dbc79-d6a3-11f0-b386-0affcf7ce151";
const EMAILJS_USER_SERVICE_ID = "service_1ldb7eq";
const EMAILJS_USER_TEMPLATE_ID = "template_esuav88";
const EMAILJS_USER_PUBLIC_KEY = "nFb2PEs76oVGHvLzR";
const EMAILJS_ADMIN_SERVICE_ID = "service_1ldb7eq";
const EMAILJS_ADMIN_TEMPLATE_ID = "template_k3wtg8b";
const EMAILJS_ADMIN_PUBLIC_KEY = "nFb2PEs76oVGHvLzR";
const SINGLE_PAYMENT_DISCOUNT_CODE = "FORMULARIO10";
const SINGLE_PAYMENT_STANDARD_DISCOUNT_PERCENT = 20;
const SINGLE_PAYMENT_PROMO_DISCOUNT_PERCENT = 30;
const HOSTED_CHECKOUT_KINDS = new Set([
    "mercado-pago",
    "mercado-pago-subscription",
    "paypal",
    "paypal-subscription",
]);
const HOSTED_SUBSCRIPTION_KINDS = new Set([
    "mercado-pago-subscription",
    "paypal-subscription",
]);
const IP_GEOLOOKUP_SERVICES = [
    {
        url: "https://ipapi.co/json/",
        pickCountryCode: (data) => data?.country_code,
    },
    {
        url: "https://ipwho.is/",
        pickCountryCode: (data) => data?.country_code,
    },
];

const COUNTRY_OPTIONS = [
    { code: "AR", label: "Argentina" },
    { code: "BO", label: "Bolivia" },
    { code: "BR", label: "Brasil" },
    { code: "CL", label: "Chile" },
    { code: "CO", label: "Colombia" },
    { code: "EC", label: "Ecuador" },
    { code: "ES", label: "España" },
    { code: "MX", label: "México" },
    { code: "PY", label: "Paraguay" },
    { code: "PE", label: "Perú" },
    { code: "UY", label: "Uruguay" },
    { code: "US", label: "Estados Unidos" },
    { code: "VE", label: "Venezuela" },
    { code: "OT", label: "Otro" },
];

const COUNTRY_PAYMENT_CONFIG = {
    AR: { currency: "ARS", locale: "es-AR" },
    BO: { currency: "BOB", locale: "es-BO" },
    BR: { currency: "BRL", locale: "pt-BR" },
    CL: { currency: "CLP", locale: "es-CL" },
    CO: { currency: "COP", locale: "es-CO" },
    EC: { currency: "USD", locale: "es-EC" },
    ES: { currency: "EUR", locale: "es-ES" },
    MX: { currency: "MXN", locale: "es-MX" },
    PY: { currency: "PYG", locale: "es-PY" },
    PE: { currency: "PEN", locale: "es-PE" },
    UY: { currency: "UYU", locale: "es-UY" },
    US: { currency: "USD", locale: "en-US" },
    VE: { currency: "VES", locale: "es-VE" },
    OT: { currency: "USD", locale: "en-US" },
};

const ZERO_DECIMAL_CURRENCIES = new Set(["ARS", "CLP", "COP", "PYG"]);

const ARGENTINA_PROVINCES = [
    "Ciudad Autónoma de Buenos Aires",
    "Buenos Aires",
    "Catamarca",
    "Chaco",
    "Chubut",
    "Córdoba",
    "Corrientes",
    "Entre Ríos",
    "Formosa",
    "Jujuy",
    "La Pampa",
    "La Rioja",
    "Mendoza",
    "Misiones",
    "Neuquén",
    "Río Negro",
    "Salta",
    "San Juan",
    "San Luis",
    "Santa Cruz",
    "Santa Fe",
    "Santiago del Estero",
    "Tierra del Fuego",
    "Tucumán",
];

const FISCAL_CONDITIONS = [
    "Consumidor Final",
    "Monotributo",
    "Responsable Inscripto",
    "Exento",
    "No Responsable",
];

const FISCAL_CONDITION_BACKEND_MAP = {
    "Consumidor Final": "consumidor_final",
    Monotributo: "monotributo",
    "Responsable Inscripto": "responsable_inscripto",
    Exento: "exento",
    "No Responsable": "no_responsable",
};

const DOCUMENT_TYPE_OPTIONS_BY_COUNTRY = {
    AR: ["DNI", "CUIT", "CUIL"],
    BO: ["CI", "NIT"],
    BR: ["CPF", "CNPJ"],
    CL: ["CI", "RUT"],
    CO: ["CC", "NIT"],
    EC: ["CI"],
    ES: ["DNI", "NIE", "Pasaporte"],
    MX: ["CURP", "RFC"],
    PE: ["DNI", "RUC"],
    PY: ["CI"],
    UY: ["CI", "RUT"],
    US: ["Pasaporte", "State ID / Driver License"],
    VE: ["CI", "RIF"],
    OT: ["Documento / ID", "Pasaporte"],
};

const ARGENTINA_TRANSFER_DETAILS = [
    {
        label: "Titular",
        value: "FUNDACION IBEROAMERICANA DE SALUD PUBLICA",
    },
    {
        label: "CUIT",
        value: "30-71660259-8",
        mono: true,
    },
    {
        label: "Número de cuenta",
        value: "0000466-8 218-1",
        mono: true,
    },
    {
        label: "CBU",
        value: "0070218020000000466815",
        mono: true,
    },
    {
        label: "Alias",
        value: "CUARTO.NAVE.CALOR",
        mono: true,
    },
];

const normalizeText = (value) => {
    if (typeof value !== "string" || !/[\u00C3\u00C2\u00E2]/.test(value)) {
        return value || "";
    }

    try {
        const bytes = Uint8Array.from(value, (char) => char.charCodeAt(0));
        const decoded = new TextDecoder("utf-8").decode(bytes);

        return decoded.includes("\uFFFD") ? value : decoded;
    } catch {
        return value;
    }
};

const loadScript = (src) =>
    new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);

        if (existing) {
            resolve();
            return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });

const hasConfiguredLink = (value) => Boolean(String(value || "").trim());

const sanitizeIdentifier = (value) =>
    String(value || "")
        .replace(/-/g, "")
        .trim();

const normalizeFiscalConditionForBackend = (value) =>
    FISCAL_CONDITION_BACKEND_MAP[value] || value || "";

const normalizeCourseNameForZoho = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

const parseJsonSafely = async (response) => {
    try {
        return await response.json();
    } catch {
        return null;
    }
};

const resolveCountryCode = (value) => {
    const nextCode = String(value || "")
        .trim()
        .toUpperCase();

    if (!nextCode) {
        return "";
    }

    return COUNTRY_OPTIONS.some((option) => option.code === nextCode)
        ? nextCode
        : "OT";
};

const detectPaymentCountryByIp = async () => {
    for (const service of IP_GEOLOOKUP_SERVICES) {
        try {
            const response = await fetch(service.url);
            const data = await parseJsonSafely(response);
            const nextCode = resolveCountryCode(
                service.pickCountryCode(data),
            );

            if (response.ok && nextCode) {
                return nextCode;
            }
        } catch {
            // Seguimos con el siguiente servicio
        }
    }

    return "AR";
};

const getEnrollmentMethodCode = (method) => {
    switch (method?.kind) {
        case "mercado-pago-subscription":
            return 1;
        case "mercado-pago":
            return 2;
        case "dlocalgo-subscription":
            return 3;
        case "card":
            return 4;
        case "transfer":
            return 5;
        case "paypal-subscription":
            return 6;
        case "paypal":
            return 7;
        default:
            return 0;
    }
};

const roundCurrencyAmount = (amount, currency) => {
    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) {
        return null;
    }

    if (ZERO_DECIMAL_CURRENCIES.has(currency)) {
        return Math.round(numericAmount);
    }

    return Number(numericAmount.toFixed(2));
};

const normalizeDiscountCode = (value) =>
    String(value || "")
        .trim()
        .toUpperCase();

const getDiscountedAmount = (baseAmount, discountPercent, currency) => {
    const numericBase = Number(baseAmount);

    if (!Number.isFinite(numericBase) || numericBase <= 0) {
        return null;
    }

    return roundCurrencyAmount(
        numericBase * (1 - Number(discountPercent || 0) / 100),
        currency,
    );
};

const formatCurrency = (amount, currency, countryCode = "AR") => {
    if (amount === null || amount === undefined || amount === "") {
        return null;
    }

    if (currency === "FOREIGN") {
        return `${Number(amount).toLocaleString("es-AR")} moneda extranjera`;
    }

    const paymentConfig =
        COUNTRY_PAYMENT_CONFIG[countryCode] || COUNTRY_PAYMENT_CONFIG.OT;

    try {
        return new Intl.NumberFormat(paymentConfig.locale, {
            style: "currency",
            currency: currency || paymentConfig.currency,
            minimumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
            maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
        }).format(Number(amount));
    } catch {
        return `${currency || paymentConfig.currency} ${Number(amount).toLocaleString("es-AR")}`;
    }
};

const getCurrencySymbol = (currency, countryCode = "AR") => {
    if (!currency || currency === "FOREIGN") {
        return "";
    }

    const paymentConfig =
        COUNTRY_PAYMENT_CONFIG[countryCode] || COUNTRY_PAYMENT_CONFIG.OT;

    try {
        const parts = new Intl.NumberFormat(paymentConfig.locale, {
            style: "currency",
            currency: currency || paymentConfig.currency,
            minimumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
            maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
        }).formatToParts(1);

        return parts.find((part) => part.type === "currency")?.value?.trim() || "";
    } catch {
        return "";
    }
};

const shouldAppendCurrencyCode = (currency, countryCode = "AR") => {
    const symbol = getCurrencySymbol(currency, countryCode)
        .replace(/\s/g, "")
        .replace(/\./g, "");

    return symbol === "$" || symbol === "";
};

const formatCurrencyWithOptionalCode = (
    amount,
    currency,
    countryCode,
    showCurrencyCode,
) => {
    const formattedAmount = formatCurrency(amount, currency, countryCode);

    return showCurrencyCode && currency
        ? `${formattedAmount} ${currency}`
        : formattedAmount;
};

const getThemeStyle = (curso) => ({
    "--checkout-primary": curso?.theme?.primary || "#1b2a4a",
    "--checkout-primary-dark": curso?.theme?.primaryDark || "#0f1b33",
    "--checkout-primary-light": curso?.theme?.primaryLight || "#5b9bd5",
    "--checkout-pale": curso?.theme?.pale || "#e2e8f0",
    "--checkout-background": "#f3f4f6",
    "--checkout-navy": curso?.theme?.navy || "#1e293b",
    "--checkout-navy-dark": curso?.theme?.navyDark || "#0f172a",
    "--checkout-hero-start": curso?.theme?.heroStart || "#0f172a",
    "--checkout-hero-mid": curso?.theme?.heroMid || "#334155",
    "--checkout-hero-end": curso?.theme?.heroEnd || "#1e293b",
});

const getInstallmentTotal = (curso) => {
    if (!curso?.precioSuscripcionMP || !curso?.cuotas) {
        return null;
    }

    return Number(curso.precioSuscripcionMP) * Number(curso.cuotas);
};

const getTransferSavings = (baseAmount, transferAmount) => {
    if (!baseAmount || !transferAmount || transferAmount >= baseAmount) {
        return 0;
    }

    return baseAmount - transferAmount;
};

const getInitialFormState = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    region: "",
    city: "",
    postalCode: "",
    street: "",
    streetNumber: "",
    floor: "",
    apartment: "",
    fiscalCondition: "Consumidor Final",
    taxId: "",
    documentType: "",
    documentNumber: "",
    cardHolder: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    acceptTerms: false,
    acceptInvoice: false,
});

const renderRequiredLabel = (label) => (
    <>
        {label} <span className="enrollment-required">*</span>
    </>
);

function CountryCombobox({ value, onChange, invalid }) {
    const wrapperRef = useRef(null);
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption =
        COUNTRY_OPTIONS.find((option) => option.code === value) || null;

    useEffect(() => {
        setQuery(selectedOption?.label || "");
    }, [selectedOption?.label]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, []);

    const filteredOptions = useMemo(() => {
        return COUNTRY_OPTIONS;
    }, []);

    const handleInputChange = (event) => {
        const nextCode = event.target.value;
        const nextOption =
            COUNTRY_OPTIONS.find((option) => option.code === nextCode) || null;

        setQuery(nextOption?.label || "");
        setIsOpen(false);
        onChange(nextCode);
    };

    return (
        <select
            ref={wrapperRef}
            required
            value={value}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            aria-expanded={isOpen}
            data-selected-label={query}
            className={invalid ? "is-invalid" : ""}
        >
            <option value="">Seleccionar</option>
            {filteredOptions.map((option) => (
                <option key={option.code} value={option.code}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

function EnrollmentPaymentLoadingCard() {
    return (
        <div className="enrollment-summary-card enrollment-summary-card-loading" aria-hidden="true">
            <div className="enrollment-summary-hero enrollment-summary-hero-loading">
                <span className="enrollment-loading-pill"></span>
                <span className="enrollment-loading-line is-title"></span>
                <span className="enrollment-loading-line is-subtitle"></span>
            </div>

            <div className="enrollment-summary-body">
                <div className="enrollment-loading-methods">
                    {[0, 1].map((item) => (
                        <div key={item} className="enrollment-loading-method">
                            <div className="enrollment-loading-method-head">
                                <span className="enrollment-loading-radio"></span>
                                <div className="enrollment-loading-copy">
                                    <span className="enrollment-loading-line is-method-title"></span>
                                    <span className="enrollment-loading-line is-method-subtitle"></span>
                                </div>
                                <span className="enrollment-loading-badge"></span>
                            </div>
                            <div className="enrollment-loading-highlight">
                                <span className="enrollment-loading-line is-label"></span>
                                <span className="enrollment-loading-line is-amount"></span>
                            </div>
                            <span className="enrollment-loading-line is-note"></span>
                        </div>
                    ))}
                </div>

                <div className="enrollment-loading-summary">
                    <span className="enrollment-loading-line is-label"></span>
                    <span className="enrollment-loading-line is-total"></span>
                </div>

                <span className="enrollment-loading-button"></span>
            </div>
        </div>
    );
}

export default function Inscripcion() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);
    const cardContainerRef = useRef(null);
    const cardFieldRef = useRef(null);
    const checkoutTokenRef = useRef(null);
    const checkoutSignatureRef = useRef("");
    const sdkInitializedRef = useRef(false);
    const family = window.location.pathname.split("/")[1];
    const curso = useMemo(() => resolveCourse(family, slug), [family, slug]);

    const [formData, setFormData] = useState(getInitialFormState);
    const [countryTouched, setCountryTouched] = useState(false);
    const [selectedMethodKey, setSelectedMethodKey] = useState("");
    const [server, setServer] = useState(routes.Vercel);
    const [paymentCountryCode, setPaymentCountryCode] = useState("AR");
    const [paymentCountryResolved, setPaymentCountryResolved] = useState(false);
    const [sdkReady, setSdkReady] = useState(false);
    const [exchangeQuote, setExchangeQuote] = useState(null);
    const [exchangeLoading, setExchangeLoading] = useState(false);
    const [exchangeError, setExchangeError] = useState("");
    const [paymentError, setPaymentError] = useState("");
    const [dlocalLoading, setDlocalLoading] = useState(false);
    const [cardSubmitting, setCardSubmitting] = useState(false);
    const [installmentsId, setInstallmentsId] = useState("");
    const [singlePaymentDiscountCode, setSinglePaymentDiscountCode] =
        useState("");

    const normalizedCourse = curso
        ? {
              ...curso,
              nombre: normalizeText(curso.nombre),
              descripcionPreview: normalizeText(curso.descripcionPreview),
              modalidad: normalizeText(curso.modalidad),
          }
        : {
              nombre: "",
              descripcionPreview: "",
              modalidad: "",
          };

    const themeStyle = getThemeStyle(curso);
    const coursePath = curso ? getCoursePath(curso) : "/educativo";
    const successPath = curso ? getEnrollmentSuccessPath(curso) : "/educativo";
    const installmentTotal = getInstallmentTotal(curso);
    const hasInternationalPricing =
        Number(curso?.precioPayPal || 0) > 0 &&
        Number(curso?.precioTransferenciaUSA || 0) > 0;
    const hasSpainPayPalPricing =
        Number(curso?.precioRegularPayPalEspana || 0) > 0 &&
        Number(curso?.precioUnPagoPayPalEspana || 0) > 0 &&
        Number(curso?.precioSuscripcionPayPalEspana || 0) > 0;
    const paymentCountryReady =
        paymentCountryResolved || !hasInternationalPricing;
    const effectivePaymentCountryCode = hasInternationalPricing
        ? paymentCountryCode
        : "AR";
    const isArgentinaPaymentFlow = effectivePaymentCountryCode === "AR";
    const isSpainPayPalPaymentFlow =
        effectivePaymentCountryCode === "ES" && hasSpainPayPalPricing;
    const isArgentinaForm = formData.country === "AR";
    const paymentConfig =
        COUNTRY_PAYMENT_CONFIG[effectivePaymentCountryCode] ||
        COUNTRY_PAYMENT_CONFIG.OT;
    const documentTypeOptions = useMemo(() => {
        return formData.country
            ? DOCUMENT_TYPE_OPTIONS_BY_COUNTRY[formData.country] ||
                  DOCUMENT_TYPE_OPTIONS_BY_COUNTRY.OT
            : [];
    }, [formData.country]);
    const baseForeignAmount = Number(curso?.precioPayPal || curso?.precioMP || 0);
    const displayForeignAmount =
        exchangeQuote?.amount && exchangeQuote?.currency
            ? exchangeQuote
            : {
                  amount: baseForeignAmount,
                  currency: "USD",
                  fromUsd: false,
                  rate: exchangeQuote?.rate || null,
                  fallbackToUsd: paymentConfig.currency !== "USD",
              };
    const paymentSidebarLoading =
        hasInternationalPricing &&
        (!paymentCountryResolved ||
            (!isArgentinaPaymentFlow &&
                !isSpainPayPalPaymentFlow &&
                exchangeLoading));
    const singlePaymentDiscountApplied =
        normalizeDiscountCode(singlePaymentDiscountCode) ===
        SINGLE_PAYMENT_DISCOUNT_CODE;

    const paymentOptions = useMemo(() => {
        if (!curso) {
            return [];
        }

        if (isArgentinaPaymentFlow) {
            const singlePaymentDiscountEnabled = curso.descuentos !== false;
            const transferBase =
                curso.precioMP || installmentTotal || curso.precioTransferencia;
            const transferDiscountPercent = singlePaymentDiscountEnabled
                ? singlePaymentDiscountApplied
                    ? SINGLE_PAYMENT_PROMO_DISCOUNT_PERCENT
                    : SINGLE_PAYMENT_STANDARD_DISCOUNT_PERCENT
                : 0;
            const transferAmount = singlePaymentDiscountEnabled
                ? singlePaymentDiscountApplied
                    ? getDiscountedAmount(
                          transferBase,
                          transferDiscountPercent,
                          "ARS",
                      ) || curso.precioTransferencia
                    : curso.precioTransferencia
                : transferBase;
            const transferSavings = singlePaymentDiscountEnabled
                ? getTransferSavings(transferBase, transferAmount)
                : 0;
            const hasTransferDiscount = Boolean(transferSavings);

            return [
                curso.precioMP
                    ? {
                          key: "mercado-pago-once",
                          kind: "mercado-pago",
                          title: "Mercado Pago · 1 pago",
                          subtitle: "Pago único",
                          amount: curso.precioMP,
                          currencyCode: "ARS",
                          totalLabel: formatCurrency(curso.precioMP, "ARS", "AR"),
                          summaryNote: "En 1 pago por Mercado Pago",
                      }
                    : null,
                curso.precioSuscripcionMP &&
                curso.cuotas > 1 &&
                hasConfiguredLink(curso.linkSuscripcionMP)
                    ? {
                          key: "mercado-pago-subscription",
                          kind: "mercado-pago-subscription",
                          title: "Mercado Pago por suscripción",
                          subtitle: `${curso.cuotas} cuotas automáticas`,
                          amount: installmentTotal,
                          monthlyAmount: curso.precioSuscripcionMP,
                          currencyCode: "ARS",
                          checkoutUrl: curso.linkSuscripcionMP,
                          totalLabel: formatCurrency(
                              installmentTotal,
                              "ARS",
                              "AR",
                          ),
                          summaryNote: `En ${curso.cuotas} cuotas de ${formatCurrency(
                              curso.precioSuscripcionMP,
                              "ARS",
                              "AR",
                          )}`,
                      }
                    : null,
                curso.precioTransferencia
                    ? {
                          key: "transfer",
                          kind: "transfer",
                          title: "Transferencia en 1 pago",
                          subtitle: hasTransferDiscount
                              ? `${transferDiscountPercent}% de descuento sobre el total`
                              : "Pago unico",
                          amount: transferAmount,
                          baseAmount: hasTransferDiscount ? transferBase : null,
                          savings: hasTransferDiscount ? transferSavings : null,
                          discountPercent: hasTransferDiscount
                              ? transferDiscountPercent
                              : null,
                          discountCodeApplied:
                              hasTransferDiscount && singlePaymentDiscountApplied,
                          currencyCode: "ARS",
                          totalLabel: formatCurrency(
                              transferAmount,
                              "ARS",
                              "AR",
                          ),
                          summaryNote: hasTransferDiscount &&
                              singlePaymentDiscountApplied
                              ? `En 1 pago por transferencia · ${SINGLE_PAYMENT_PROMO_DISCOUNT_PERCENT}% OFF aplicado`
                              : "En 1 pago por transferencia",
                      }
                    : null,
            ]
                .filter(Boolean)
                .filter((option) => option.key !== "mercado-pago-once");
        }

        if (isSpainPayPalPaymentFlow) {
            const paypalRegularAmount = Number(
                curso.precioRegularPayPalEspana || curso.precioPayPal || 0,
            );
            const paypalSingleAmount = Number(
                curso.precioUnPagoPayPalEspana ||
                    curso.precioTransferenciaUSA ||
                    0,
            );
            const paypalMonthlyAmount = Number(
                curso.precioSuscripcionPayPalEspana ||
                    curso.precioSuscripcionPayPal ||
                    0,
            );
            const paypalInstallmentsCount = Number(curso.cuotas || 0);
            const paypalSubscriptionTotal =
                paypalMonthlyAmount && paypalInstallmentsCount
                    ? roundCurrencyAmount(
                          paypalMonthlyAmount * paypalInstallmentsCount,
                          "USD",
                      )
                    : null;
            const paypalSavings = getTransferSavings(
                paypalRegularAmount,
                paypalSingleAmount,
            );
            const paypalDiscountPercent =
                paypalRegularAmount && paypalSavings
                    ? Math.round((paypalSavings / paypalRegularAmount) * 100)
                    : null;
            const showPayPalCurrencyCode = true;

            return [
                paypalSingleAmount && hasConfiguredLink(curso.linkPayPalEspana)
                    ? {
                          key: "paypal-once-spain",
                          kind: "paypal",
                          title: "PayPal",
                          subtitle: "1 pago",
                          amount: paypalSingleAmount,
                          baseAmount: paypalSavings ? paypalRegularAmount : null,
                          savings: paypalSavings || null,
                          discountPercent: paypalDiscountPercent,
                          currencyCode: "USD",
                          countryCode: "US",
                          showCurrencyCodeSuffix: showPayPalCurrencyCode,
                          checkoutUrl: curso.linkPayPalEspana,
                          totalLabel: formatCurrencyWithOptionalCode(
                              paypalSingleAmount,
                              "USD",
                              "US",
                              showPayPalCurrencyCode,
                          ),
                          summaryNote: "1 pago por PayPal",
                      }
                    : null,
                paypalSubscriptionTotal &&
                paypalMonthlyAmount &&
                paypalInstallmentsCount &&
                hasConfiguredLink(curso.linkSuscripcionPayPalEspana)
                    ? {
                          key: "paypal-subscription-spain",
                          kind: "paypal-subscription",
                          title: "PayPal",
                          subtitle: "Por suscripcion",
                          amount: paypalSubscriptionTotal,
                          monthlyAmount: paypalMonthlyAmount,
                          installmentsCount: paypalInstallmentsCount,
                          currencyCode: "USD",
                          countryCode: "US",
                          showCurrencyCodeSuffix: showPayPalCurrencyCode,
                          checkoutUrl: curso.linkSuscripcionPayPalEspana,
                          totalLabel: formatCurrencyWithOptionalCode(
                              paypalSubscriptionTotal,
                              "USD",
                              "US",
                              showPayPalCurrencyCode,
                          ),
                          summaryNote: `En ${paypalInstallmentsCount} cuotas de ${formatCurrencyWithOptionalCode(
                              paypalMonthlyAmount,
                              "USD",
                              "US",
                              showPayPalCurrencyCode,
                          )}`,
                      }
                    : null,
            ].filter(Boolean);
        }

        const singlePaymentDiscountEnabled = curso.descuentos !== false;
        const foreignCardBaseAmount =
            displayForeignAmount.amount || baseForeignAmount;
        const foreignCardDiscountBase = singlePaymentDiscountEnabled
            ? Number(curso.precioTransferenciaUSA || baseForeignAmount) || 0
            : foreignCardBaseAmount;
        const foreignCardDiscountPercent = singlePaymentDiscountEnabled
            ? singlePaymentDiscountApplied
                ? SINGLE_PAYMENT_PROMO_DISCOUNT_PERCENT
                : SINGLE_PAYMENT_STANDARD_DISCOUNT_PERCENT
            : 0;
        const foreignCardAmount = singlePaymentDiscountEnabled
            ? singlePaymentDiscountApplied
                ? getDiscountedAmount(
                      foreignCardBaseAmount,
                      foreignCardDiscountPercent,
                      displayForeignAmount.currency || "USD",
                  )
                : roundCurrencyAmount(
                      foreignCardDiscountBase * (displayForeignAmount.rate || 1),
                      displayForeignAmount.currency || "USD",
                  )
            : foreignCardBaseAmount;
        const resolvedForeignCardAmount =
            foreignCardAmount || foreignCardBaseAmount;
        const foreignCardSavings = singlePaymentDiscountEnabled
            ? getTransferSavings(
                  foreignCardBaseAmount,
                  resolvedForeignCardAmount,
              )
            : 0;
        const hasForeignCardDiscount = Boolean(foreignCardSavings);
        const foreignRecurringInstallmentAmount =
            curso.cuotas > 1
                ? roundCurrencyAmount(
                      (baseForeignAmount / Number(curso.cuotas || 1)) *
                          (displayForeignAmount.rate || 1),
                      displayForeignAmount.currency || "USD",
                  )
                : null;
        const foreignRecurringTotalAmount =
            foreignRecurringInstallmentAmount && curso.cuotas
                ? roundCurrencyAmount(
                      foreignRecurringInstallmentAmount *
                          Number(curso.cuotas || 1),
                      displayForeignAmount.currency || "USD",
                  )
                : null;

        return [
            resolvedForeignCardAmount
                ? {
                    key: "international-card",
                    kind: "card",
                    title: "Un pago",
                    subtitle: "Con tarjeta de débito o crédito",
                    amount: resolvedForeignCardAmount,
                    baseAmount: hasForeignCardDiscount
                        ? foreignCardBaseAmount
                        : null,
                    savings: hasForeignCardDiscount
                        ? foreignCardSavings
                        : null,
                    discountPercent: hasForeignCardDiscount
                        ? foreignCardDiscountPercent
                        : null,
                    discountCodeApplied:
                        hasForeignCardDiscount && singlePaymentDiscountApplied,
                    currencyCode: displayForeignAmount.currency,
                    countryCode: effectivePaymentCountryCode,
                    exchangeRate: displayForeignAmount.rate,
                    totalLabel: formatCurrency(
                        resolvedForeignCardAmount,
                        displayForeignAmount.currency,
                        effectivePaymentCountryCode,
                    ),
                     summaryNote: hasForeignCardDiscount &&
                        singlePaymentDiscountApplied
                        ? `1 pago con tarjeta de credito o debito · ${SINGLE_PAYMENT_PROMO_DISCOUNT_PERCENT}% OFF aplicado`
                        : "1 pago con tarjeta de credito o debito",
                 }
                : null,
            foreignRecurringInstallmentAmount &&
            foreignRecurringTotalAmount &&
            Number(curso.cuotas || 0) > 1
                ? {
                    key: "international-recurring-card",
                    kind: "dlocalgo-subscription",
                    title: `${curso.cuotas} cuotas`,
                    subtitle: "Pago recurrente con tarjeta",
                    amount: foreignRecurringTotalAmount,
                    recurringAmount: foreignRecurringInstallmentAmount,
                    monthlyAmount: foreignRecurringInstallmentAmount,
                    installmentsCount: Number(curso.cuotas || 0),
                    currencyCode: displayForeignAmount.currency,
                    countryCode: effectivePaymentCountryCode,
                    exchangeRate: displayForeignAmount.rate,
                    totalLabel: formatCurrency(
                        foreignRecurringTotalAmount,
                        displayForeignAmount.currency,
                        effectivePaymentCountryCode,
                    ),
                    summaryNote: `En ${curso.cuotas} cuotas de ${formatCurrency(
                        foreignRecurringInstallmentAmount,
                        displayForeignAmount.currency,
                        effectivePaymentCountryCode,
                    )}`,
                  }
                : null,
        ].filter(Boolean);
    }, [
        baseForeignAmount,
        curso,
        displayForeignAmount.amount,
        displayForeignAmount.currency,
        displayForeignAmount.rate,
        installmentTotal,
        effectivePaymentCountryCode,
        isArgentinaPaymentFlow,
        isSpainPayPalPaymentFlow,
        singlePaymentDiscountApplied,
    ]);

    const selectedMethod =
        paymentOptions.find((option) => option.key === selectedMethodKey) ||
        paymentOptions[0] ||
        null;
    const needsEmbeddedCard =
        selectedMethod?.kind === "card" ||
        selectedMethod?.kind === "dlocalgo-subscription";

    useEffect(() => {
        if (!paymentCountryReady) {
            return;
        }

        const nextDefault = isArgentinaPaymentFlow
            ? paymentOptions.find((option) => option.kind === "transfer")?.key ||
              paymentOptions[0]?.key ||
              ""
            : paymentOptions.find((option) => option.kind === "card")?.key ||
              paymentOptions.find((option) => option.kind === "paypal")?.key ||
              paymentOptions[0]?.key ||
              "";

        if (
            !selectedMethodKey ||
            !paymentOptions.some((option) => option.key === selectedMethodKey)
        ) {
            setSelectedMethodKey(nextDefault);
        }
    }, [
        isArgentinaPaymentFlow,
        paymentCountryReady,
        paymentOptions,
        selectedMethodKey,
    ]);

    useEffect(() => {
        if (!formData.country || isArgentinaForm) {
            return;
        }

        if (
            formData.documentType &&
            !documentTypeOptions.includes(formData.documentType)
        ) {
            setFormData((current) => ({
                ...current,
                documentType: "",
            }));
        }
    }, [
        documentTypeOptions,
        formData.country,
        formData.documentType,
        isArgentinaForm,
    ]);

    useEffect(() => {
        let active = true;

        fetch(`${routes.Hostinger}/ping`)
            .then(async (response) => {
                const data = await response.text();

                if (!active) {
                    return;
                }

                if (data === "pong") {
                    setServer(routes.Hostinger);
                } else {
                    setServer(routes.Vercel);
                }
            })
            .catch(() => {
                if (active) {
                    setServer(routes.Vercel);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        loadScript(DLOCAL_SMART_FIELDS_SRC)
            .then(() => {
                if (active) {
                    setSdkReady(true);
                }
            })
            .catch(() => {
                if (active) {
                    setPaymentError(
                        "No pudimos cargar el formulario seguro de tarjeta.",
                    );
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const detectCountry = async () => {
            try {
                const detectedCode = await detectPaymentCountryByIp();

                if (isCancelled) {
                    return;
                }

                setPaymentCountryCode(detectedCode);
            } catch {
                if (!isCancelled) {
                    setPaymentCountryCode("AR");
                }
            } finally {
                if (!isCancelled) {
                    setPaymentCountryResolved(true);
                }
            }
        };

        detectCountry();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        if (
            !paymentCountryReady ||
            !curso ||
            isArgentinaPaymentFlow ||
            isSpainPayPalPaymentFlow
        ) {
            setExchangeQuote(null);
            setExchangeError("");
            setExchangeLoading(false);
            return undefined;
        }

        if (!baseForeignAmount) {
            setExchangeQuote(null);
            setExchangeError("");
            setExchangeLoading(false);
            return undefined;
        }

        if (paymentConfig.currency === "USD") {
            setExchangeQuote({
                amount: roundCurrencyAmount(baseForeignAmount, "USD"),
                currency: "USD",
                rate: 1,
                fallbackToUsd: false,
            });
            setExchangeError("");
            setExchangeLoading(false);
            return undefined;
        }

        const loadExchangeRate = async () => {
            try {
                setExchangeLoading(true);
                setExchangeError("");

                const response = await fetch(`${server}/dlocal/exchange-rates`);
                const data = await response.json();

                console.log("[exchange-rates] status:", response.status);
                console.log("[exchange-rates] data:", data);
                console.log("[exchange-rates] currency buscada:", paymentConfig.currency);
                console.log("[exchange-rates] rawRate:", data?.rates?.[paymentConfig.currency]);

                if (!response.ok) {
                    throw new Error(data?.error || "exchange-rates failed");
                }

                const rawRate = data?.rates?.[paymentConfig.currency];

                if (rawRate == null || !Number.isFinite(Number(rawRate)) || Number(rawRate) <= 0) {
                    throw new Error("exchange-rate-unavailable");
                }

                const rate = Number(rawRate);

                if (isCancelled) {
                    return;
                }

                setExchangeQuote({
                    amount: roundCurrencyAmount(
                        baseForeignAmount * rate,
                        paymentConfig.currency,
                    ),
                    currency: paymentConfig.currency,
                    rate,
                    fallbackToUsd: false,
                });
            } catch {
                if (isCancelled) {
                    return;
                }

                setExchangeQuote({
                    amount: roundCurrencyAmount(baseForeignAmount, "USD"),
                    currency: "USD",
                    rate: null,
                    fallbackToUsd: true,
                });
                setExchangeError(
                    "No pudimos convertir el precio a tu moneda local. Te mostramos el valor internacional.",
                );
            } finally {
                if (!isCancelled) {
                    setExchangeLoading(false);
                }
            }
        };

        loadExchangeRate();

        return () => {
            isCancelled = true;
        };
    }, [
        baseForeignAmount,
        curso,
        isArgentinaPaymentFlow,
        isSpainPayPalPaymentFlow,
        paymentCountryReady,
        paymentConfig.currency,
        server,
    ]);

    const resetDlocalCheckout = () => {
        checkoutTokenRef.current = null;
        checkoutSignatureRef.current = "";
        cardFieldRef.current = null;
        sdkInitializedRef.current = false;
        setInstallmentsId("");

        if (cardContainerRef.current) {
            cardContainerRef.current.innerHTML = "";
        }
    };

    const initializeSmartFields = async (merchantCheckoutToken) => {
        if (!window.dlocalGo) {
            throw new Error("dlocal-sdk-missing");
        }

        if (!cardContainerRef.current) {
            throw new Error("dlocal-card-container-missing");
        }

        cardContainerRef.current.innerHTML = "";

        await window.dlocalGo.initialize(
            DLOCAL_SMART_FIELDS_API_KEY,
            merchantCheckoutToken,
        );

        const fields = window.dlocalGo.fields();
        const cardField = fields.create("card", {
            style: {
                base: {
                    fontSize: "13px",
                    lineHeight: "20px",
                    fontFamily: "Inter, Arial, sans-serif",
                    color: "#0f172a",
                    "::placeholder": {
                        color: "#94a3b8",
                    },
                    iconColor: "#94a3b800",
                },
                invalid: {
                    color: "#dc2626",
                    iconColor: "#dc262600",
                },
            },
            placeholder: {
                number: "Numero de tarjeta",
                expiration: "MM/AA",
                cvv: "CVV",
            },
        });

        cardField.mount(cardContainerRef.current);
        cardFieldRef.current = cardField;
        sdkInitializedRef.current = true;

        if (typeof window.dlocalGo.onInstallmentsChange === "function") {
            window.dlocalGo.onInstallmentsChange((items) => {
                const list = Array.isArray(items) ? items : [];
                setInstallmentsId(list[0]?.id || "");
            });
        }
    };

    useEffect(() => {
        let isCancelled = false;

        const isArgentinaDlocal =
            isArgentinaPaymentFlow &&
            selectedMethod?.kind === "dlocalgo-subscription";
        if (
            !paymentCountryReady ||
            (isArgentinaPaymentFlow && !isArgentinaDlocal) ||
            !needsEmbeddedCard ||
            !sdkReady ||
            !(selectedMethod?.recurringAmount || selectedMethod?.amount) ||
            !selectedMethod?.currencyCode ||
            (!isArgentinaDlocal && exchangeLoading)
        ) {
            resetDlocalCheckout();
            setDlocalLoading(false);
            return undefined;
        }

        const checkoutCountryCode =
            effectivePaymentCountryCode === "OT"
                ? "US"
                : effectivePaymentCountryCode;
        const nextSignature = [
            server,
            checkoutCountryCode,
            selectedMethod.kind,
            selectedMethod.currencyCode,
            selectedMethod.recurringAmount || selectedMethod.amount,
        ].join(":");

        if (
            checkoutSignatureRef.current === nextSignature &&
            sdkInitializedRef.current
        ) {
            return undefined;
        }

        const initializeCheckout = async () => {
            try {
                setDlocalLoading(true);
                setPaymentError("");
                resetDlocalCheckout();

                const endpoint =
                    selectedMethod.kind === "dlocalgo-subscription"
                        ? `${server}/dlocal/recurring/initialize`
                        : `${server}/dlocal/create-payment`;
                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        amount: Number(
                            selectedMethod.recurringAmount || selectedMethod.amount,
                        ),
                        currency: selectedMethod.currencyCode,
                        country: checkoutCountryCode,
                        installmentsCount:
                            selectedMethod.installmentsCount || undefined,
                        installmentAmount:
                            selectedMethod.recurringAmount || undefined,
                        courseId: curso?.id,
                        courseName: normalizedCourse.nombre,
                        courseSlug: slug,
                        courseFamily: family,
                        customerEmail: formData.email || undefined,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data?.error || "dlocal-create-payment-failed");
                }

                if (!data?.merchant_checkout_token) {
                    throw new Error("dlocal-missing-checkout-token");
                }

                if (isCancelled) {
                    return;
                }

                checkoutTokenRef.current = data.merchant_checkout_token;
                checkoutSignatureRef.current = nextSignature;
                await initializeSmartFields(data.merchant_checkout_token);
            } catch (err) {
                console.error("[dlocal] initializeCheckout error:", err);
                if (!isCancelled) {
                    setPaymentError(
                        selectedMethod.kind === "dlocalgo-subscription"
                            ? "No pudimos preparar la suscripción. Probá nuevamente en unos minutos."
                            : "No pudimos inicializar el pago con tarjeta. Probá nuevamente en unos segundos.",
                    );
                }
            } finally {
                if (!isCancelled) {
                    setDlocalLoading(false);
                }
            }
        };

        initializeCheckout();

        return () => {
            isCancelled = true;
        };
    }, [
        exchangeLoading,
        effectivePaymentCountryCode,
        isArgentinaPaymentFlow,
        paymentCountryReady,
        sdkReady,
        needsEmbeddedCard,
        normalizedCourse.nombre,
        curso?.id,
        family,
        formData.email,
        selectedMethod?.amount,
        selectedMethod?.currencyCode,
        selectedMethod?.kind,
        selectedMethod?.recurringAmount,
        selectedMethod?.installmentsCount,
        server,
        slug,
    ]);

    useEffect(() => {
        setPaymentError("");
    }, [paymentCountryCode, selectedMethodKey]);

    const handleFieldChange = (field) => (event) => {
        const value =
            event.target.type === "checkbox"
                ? event.target.checked
                : event.target.value;

        setFormData((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleCountryChange = (nextCountryCode) => {
        setFormData((current) => ({
            ...current,
            country: nextCountryCode,
        }));

        if (nextCountryCode) {
            setCountryTouched(false);
        }
    };

    const handleDiscountCodeChange = (event) => {
        setSinglePaymentDiscountCode(event.target.value);
    };

    const getCountryLabel = (countryCode) =>
        COUNTRY_OPTIONS.find((option) => option.code === countryCode)?.label ||
        countryCode ||
        "";

    const getEnrollmentPaymentLabel = () => {
        if (!selectedMethod) {
            return "";
        }

        if (selectedMethod.title && selectedMethod.subtitle) {
            return `${selectedMethod.title} - ${selectedMethod.subtitle}`;
        }

        return selectedMethod.title || selectedMethod.kind || "";
    };

    const sendEnrollmentEmails = async (sendToStudent) => {
        const addressLine1 = [
            formData.street,
            formData.streetNumber,
            formData.floor,
            formData.apartment,
        ]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .join(" ");
        const addressLine2 = [
            formData.city,
            formData.postalCode ? `CP:${formData.postalCode}` : "",
            formData.region,
            getCountryLabel(formData.country),
        ]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .join(", ");
        const fiscalOrDocument =
            formData.country === "AR"
                ? formData.fiscalCondition
                : formData.documentType;
        const taxIdOrDocument =
            formData.country === "AR"
                ? formData.taxId
                : formData.documentNumber;
        const documentTypeForEmail =
            formData.country === "AR" ? "CUIT" : formData.documentType;
        const documentNumberForEmail =
            formData.country === "AR"
                ? formData.taxId
                : formData.documentNumber;
        const priceInPesos = Number(curso?.precioMP || 0);
        const priceInDollars = Number(curso?.precioPayPal || 0);
        const requests = [];

        if (sendToStudent) {
            requests.push(
                emailjs.send(
                    EMAILJS_USER_SERVICE_ID,
                    EMAILJS_USER_TEMPLATE_ID,
                    {
                        curso: normalizedCourse.nombre,
                        email: formData.email,
                    },
                    EMAILJS_USER_PUBLIC_KEY,
                ),
            );
        }

        // emailjs.send("service_folxspa","template_eomeuar", { CAMBIAR KEY DE ABAJO TAMB
        requests.push(
            emailjs.send(
                EMAILJS_ADMIN_SERVICE_ID,
                EMAILJS_ADMIN_TEMPLATE_ID,
                {
                    nombre: formData.firstName,
                    apellido: formData.lastName,
                    email: "secacademica@fisp.org.ar",
                    usuarioEmail: formData.email,
                    telefono: formData.phone,
                    direccion: addressLine1,
                    direccion2: addressLine2,
                    condicionFiscal: fiscalOrDocument,
                    cuit: taxIdOrDocument,
                    curso: normalizedCourse.nombre,
                    precioPesos: priceInPesos,
                    precioDolares: priceInDollars,
                    tipoDocumento: documentTypeForEmail,
                    numeroDocumento: documentNumberForEmail,
                    metodoPago: getEnrollmentPaymentLabel(),
                },
                EMAILJS_ADMIN_PUBLIC_KEY,
            ),
        );

        try {
            await Promise.allSettled(requests);
        } catch (error) {
            console.error("Error enviando mails de inscripción:", error);
        }
    };

    const handleUploadFormulario = async () => {
        if (!curso || !selectedMethod) {
            return;
        }

        const apiBase = server || routes.Vercel;
        const normalizedEmail = String(formData.email || "").trim();
        const clientName = `${formData.lastName} ${formData.firstName}`.trim();
        const normalizedDocumentValue = sanitizeIdentifier(
            isArgentinaForm ? formData.taxId : formData.documentNumber,
        );
        const normalizedFiscalOrDocument = isArgentinaForm
            ? normalizeFiscalConditionForBackend(formData.fiscalCondition)
            : formData.documentType;
        const enrollmentMethodCode = getEnrollmentMethodCode(selectedMethod);
        const totalAmount = Number(
            selectedMethod.amount || selectedMethod.recurringAmount || 0,
        );

        const fetchJson = async (url, options) => {
            const response = await fetch(url, options);
            const data = await parseJsonSafely(response);

            if (!response.ok) {
                throw new Error(
                    data?.error || `request-failed:${response.status}`,
                );
            }

            return data;
        };

        try {
            let clientCode = null;
            let clientDisplayName = clientName;
            const existingClient = await fetchJson(
                `${apiBase}/clientes/email/${encodeURIComponent(normalizedEmail)}`,
            );

            if (Array.isArray(existingClient) && existingClient.length > 0) {
                clientCode = existingClient[0].codigo;
                clientDisplayName = existingClient[0].nombre || clientName;
            } else {
                let isCodeInUse = true;
                let attempts = 0;

                while (isCodeInUse && attempts < 50) {
                    attempts += 1;
                    clientCode = Math.floor(Math.random() * 5000) + 5000;

                    const codeCheck = await fetchJson(
                        `${apiBase}/clientes/codigo/${clientCode}`,
                    );
                    isCodeInUse =
                        Array.isArray(codeCheck) && codeCheck.length > 0;
                }

                if (!clientCode || isCodeInUse) {
                    throw new Error("no-client-code-available");
                }

                const payload = {
                    codigo: clientCode,
                    nombre: clientDisplayName,
                    pais: getCountryLabel(formData.country),
                    provincia: formData.region,
                    localidad: formData.city,
                    cp: formData.postalCode,
                    calle: formData.street,
                    numero: formData.streetNumber,
                    piso: formData.floor,
                    depto: formData.apartment,
                    email: normalizedEmail,
                    telefono: formData.phone,
                    condicion_fiscal: normalizedFiscalOrDocument,
                    cuit: normalizedDocumentValue,
                };

                await fetchJson(`${apiBase}/clientes`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });
            }

            const clienteCursoPayload = {
                codigo_cliente: clientCode,
                nombre_cliente: clientDisplayName,
                id_curso: curso.id,
                codigo_curso_T: curso.codigoT,
                nombre_curso: normalizedCourse.nombre,
                inicio_curso: curso.inicio || null,
                precio_curso_pesos: Number(curso.precioMP || 0),
                precio_curso_dolares: Number(curso.precioPayPal || 0),
                metodo_pago: enrollmentMethodCode,
                total_pagar: totalAmount,
                cuotas: Number(curso.cuotas || 0),
                cuotas_pagas: 0,
                precio_cuotas:
                    selectedMethod.kind === "mercado-pago-subscription"
                        ? Number(curso.precioSuscripcionMP || 0)
                        : selectedMethod.kind === "dlocalgo-subscription"
                          ? Number(selectedMethod.recurringAmount || 0)
                          : selectedMethod.kind === "paypal-subscription"
                            ? Number(selectedMethod.monthlyAmount || 0)
                          : 0,
            };

            await fetchJson(`${apiBase}/clientes-cursos`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(clienteCursoPayload),
            });
        } catch (error) {
            console.error("Error guardando formulario de inscripción:", error);
        }
    };

    const sendZohoLead = async () => {
        const apiBase = server || routes.Vercel;
        const payload = new URLSearchParams({
            firstName: String(formData.firstName || "").trim(),
            lastName: String(formData.lastName || "").trim(),
            email: String(formData.email || "").trim(),
            phone: String(formData.phone || "").trim(),
            courseName: normalizeCourseNameForZoho(normalizedCourse.nombre),
        });

        try {
            if (typeof navigator !== "undefined" && navigator.sendBeacon) {
                const queued = navigator.sendBeacon(`${apiBase}/zoho/lead`, payload);

                if (queued) {
                    return;
                }
            }

            const response = await fetch(`${apiBase}/zoho/lead`, {
                method: "POST",
                keepalive: true,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: payload.toString(),
            });

            if (!response.ok) {
                throw new Error(`request-failed:${response.status}`);
            }
        } catch (error) {
            console.error("Error enviando lead a Zoho:", error);
        }
    };

    const buildHostedCheckoutUrl = (baseUrl) => {
        if (!baseUrl) {
            return "";
        }

        return baseUrl;
    };

    const handleHostedCheckout = () => {
        const checkoutUrl = buildHostedCheckoutUrl(selectedMethod?.checkoutUrl);

        if (!checkoutUrl) {
            setPaymentError(
                "Todavia no configuramos el link de pago para esta opcion.",
            );
            return;
        }

        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
    };

    const handleTransferFlow = async () => {
        await sendEnrollmentEmails(true);

        navigate(`${successPath}?status=transfer&method=transfer`, {
            state: {
                courseName: normalizedCourse.nombre,
                email: formData.email,
                paymentMethodLabel: selectedMethod?.title || "Transferencia",
                paymentAmount: selectedMethod?.totalLabel || "",
                discountCodeApplied: Boolean(selectedMethod?.discountCodeApplied),
                discountPercent: selectedMethod?.discountPercent || null,
                transferDetails:
                    selectedMethod?.transferDetails ||
                    ARGENTINA_TRANSFER_DETAILS,
            },
        });
    };

    const handleCardPayment = async () => {
        const checkoutCountryCode =
            effectivePaymentCountryCode === "OT"
                ? "US"
                : effectivePaymentCountryCode;
        const cardholderName =
            formData.cardHolder.trim() ||
            `${formData.firstName} ${formData.lastName}`.trim();

        if (!sdkInitializedRef.current || !cardFieldRef.current) {
            setPaymentError(
                "El formulario de tarjeta todavia no esta listo. Espera unos segundos e intenta de nuevo.",
            );
            return;
        }

        try {
            setCardSubmitting(true);
            setPaymentError("");

            const tokenResponse = await window.dlocalGo.createCardToken(
                cardFieldRef.current,
                { name: cardholderName },
            );
            const cardToken = tokenResponse?.token;

            if (!cardToken || !checkoutTokenRef.current) {
                throw new Error("dlocal-card-token-missing");
            }

            const response = await fetch(`${server}/dlocal/confirm-payment`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    checkoutToken: checkoutTokenRef.current,
                    cardToken,
                    clientFirstName: formData.firstName,
                    clientLastName: formData.lastName,
                    clientDocumentType: isArgentinaForm
                        ? "DNI"
                        : formData.documentType,
                    clientDocument: isArgentinaForm
                        ? formData.taxId
                        : formData.documentNumber,
                    clientEmail: formData.email,
                    installmentsId: installmentsId || undefined,
                    country: checkoutCountryCode,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.error || "dlocal-confirm-payment-failed");
            }

            if (data?.redirect_url) {
                await sendEnrollmentEmails(true);
                window.location.href = data.redirect_url;
                return;
            }

            await sendEnrollmentEmails(true);
            navigate(`${successPath}?status=success&method=card`, {
                state: {
                    courseName: normalizedCourse.nombre,
                    email: formData.email,
                    paymentMethodLabel: selectedMethod?.title || "Tarjeta",
                    paymentAmount: selectedMethod?.totalLabel || "",
                    discountCodeApplied: Boolean(selectedMethod?.discountCodeApplied),
                    discountPercent: selectedMethod?.discountPercent || null,
                },
            });
        } catch {
            setPaymentError(
                "No pudimos procesar el pago con tarjeta. Revisa los datos e intenta nuevamente.",
            );
        } finally {
            setCardSubmitting(false);
        }
    };

    const handleRecurringSubscription = async () => {
        const checkoutCountryCode =
            effectivePaymentCountryCode === "OT"
                ? "US"
                : effectivePaymentCountryCode;
        const cardholderName =
            formData.cardHolder.trim() ||
            `${formData.firstName} ${formData.lastName}`.trim();

        if (!sdkInitializedRef.current || !cardFieldRef.current) {
            setPaymentError(
                "El formulario de suscripcion todavia no esta listo. Espera unos segundos e intenta de nuevo.",
            );
            return;
        }

        try {
            setCardSubmitting(true);
            setPaymentError("");

            const tokenResponse = await window.dlocalGo.createCardToken(
                cardFieldRef.current,
                { name: cardholderName },
            );
            const cardToken = tokenResponse?.token;

            if (!cardToken || !checkoutTokenRef.current) {
                throw new Error("dlocal-recurring-card-token-missing");
            }

            const response = await fetch(`${server}/dlocal/recurring/subscribe`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    checkoutToken: checkoutTokenRef.current,
                    cardToken,
                    courseId: curso?.id,
                    courseName: normalizedCourse.nombre,
                    courseSlug: slug,
                    courseFamily: family,
                    installmentsCount: selectedMethod?.installmentsCount,
                    installmentAmount: selectedMethod?.recurringAmount,
                    currency: selectedMethod?.currencyCode,
                    country: checkoutCountryCode,
                    clientFirstName: formData.firstName,
                    clientLastName: formData.lastName,
                    clientDocumentType: isArgentinaForm
                        ? "DNI"
                        : formData.documentType,
                    clientDocument: isArgentinaForm
                        ? formData.taxId
                        : formData.documentNumber,
                    clientEmail: formData.email,
                    clientPhone: formData.phone,
                    clientCountry: formData.country,
                    clientRegion: formData.region,
                    clientCity: formData.city,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.error || "dlocal-recurring-subscribe-failed");
            }

            if (data?.redirect_url) {
                await sendEnrollmentEmails(true);
                window.location.href = data.redirect_url;
                return;
            }

            await sendEnrollmentEmails(true);
            navigate(`${successPath}?status=success&method=recurring`, {
                state: {
                    courseName: normalizedCourse.nombre,
                    email: formData.email,
                    paymentMethodLabel:
                        selectedMethod?.title || "Suscripcion recurrente",
                    paymentAmount: selectedMethod?.summaryNote || "",
                    recurringSummary: selectedMethod?.summaryNote || "",
                },
            });
        } catch {
            setPaymentError(
                "No pudimos iniciar la suscripcion recurrente. Revisa los datos e intenta nuevamente.",
            );
        } finally {
            setCardSubmitting(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.country) {
            setCountryTouched(true);
            return;
        }

        if (!formRef.current?.reportValidity()) {
            return;
        }

        if (!selectedMethod) {
            return;
        }

        void sendZohoLead();
        await handleUploadFormulario();

        if (HOSTED_CHECKOUT_KINDS.has(selectedMethod.kind)) {
            await sendEnrollmentEmails(
                !HOSTED_SUBSCRIPTION_KINDS.has(selectedMethod.kind),
            );
            handleHostedCheckout();
            return;
        }

        if (selectedMethod.kind === "transfer") {
            await handleTransferFlow();
            return;
        }

        if (selectedMethod.kind === "card") {
            await handleCardPayment();
            return;
        }

        if (selectedMethod.kind === "dlocalgo-subscription") {
            await handleRecurringSubscription();
        }
    };

    const formatSelectedMethodAmount = (amount) =>
        formatCurrencyWithOptionalCode(
            amount,
            selectedMethod?.currencyCode || "ARS",
            selectedMethod?.countryCode || "AR",
            selectedMethod?.showCurrencyCodeSuffix,
        );
    const summaryDiscount = selectedMethod?.savings
        ? formatSelectedMethodAmount(selectedMethod.savings)
        : null;
    const summaryBaseAmount = selectedMethod?.baseAmount
        ? formatSelectedMethodAmount(selectedMethod.baseAmount)
        : null;

    if (!curso) {
        return <Navigate to="/educativo" replace />;
    }

    if (curso.cuposLlenos) {
        return <Navigate to={coursePath} replace />;
    }

    return (
        <div className="course-enrollment" style={themeStyle}>
            <SiteNavbar
                activeSection="educativo"
                navTheme={curso.theme}
                checkoutMode
                secureBadgeSrc={compraSeguraBadge}
                secureBadgeAlt="Inscripción segura"
                secureLabel="Inscripción segura · SSL"
            />

            <section className="enrollment-progress">
                <div className="enrollment-shell">
                    <div className="enrollment-steps">
                        <div className="enrollment-step is-done">
                            <span className="enrollment-step-circle">1</span>
                            <span>Programa</span>
                        </div>
                        <span className="enrollment-step-line"></span>
                        <div className="enrollment-step is-active">
                            <span className="enrollment-step-circle">2</span>
                            <span>Datos y pago</span>
                        </div>
                        <span className="enrollment-step-line"></span>
                        <div className="enrollment-step">
                            <span className="enrollment-step-circle">3</span>
                            <span>Confirmación</span>
                        </div>
                    </div>
                </div>
            </section>

            <main className="enrollment-main">
                <div className="enrollment-shell">
                    <div className="enrollment-breadcrumbs">
                        <Link to="/">Inicio</Link>
                        <span>/</span>
                        <Link to="/educativo">FISP Educativo</Link>
                        <span>/</span>
                        <Link to={coursePath}>{normalizedCourse.nombre}</Link>
                        <span>/</span>
                        <span>Inscripción</span>
                    </div>

                    <form
                        ref={formRef}
                        id="formulario"
                        className="enrollment-grid"
                        onSubmit={handleSubmit}
                    >
                        <section className="enrollment-form-card">
                            <div className="enrollment-card-head">
                                <h1>Datos de inscripción</h1>
                                <p>
                                    Completa el formulario para continuar con el pago.
                                    Los datos ingresados serán utilizados por FISP para
                                    la emisión de la factura correspondiente.
                                </p>
                            </div>

                            <div className="enrollment-field-grid two">
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Nombre")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.firstName}
                                        onChange={handleFieldChange("firstName")}
                                        placeholder=""
                                    />
                                </label>
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Apellido")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.lastName}
                                        onChange={handleFieldChange("lastName")}
                                        placeholder=""
                                    />
                                </label>
                            </div>

                            <div className="enrollment-field-grid two">
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Email")}</span>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleFieldChange("email")}
                                        placeholder="@"
                                    />
                                </label>
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Teléfono")}</span>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={handleFieldChange("phone")}
                                        placeholder="+54 11"
                                    />
                                </label>
                            </div>

                            <div className="enrollment-field-grid three">
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("País")}</span>
                                    <CountryCombobox
                                        value={formData.country}
                                        onChange={handleCountryChange}
                                        invalid={countryTouched && !formData.country}
                                    />
                                    {countryTouched && !formData.country ? (
                                        <small className="enrollment-field-error">
                                            Seleccioná un país de la lista.
                                        </small>
                                    ) : null}
                                </label>
                                <label className="enrollment-field">
                                    <span>
                                        {renderRequiredLabel(
                                            isArgentinaForm
                                                ? "Provincia"
                                                : "Estado / región",
                                        )}
                                    </span>
                                    {isArgentinaForm ? (
                                        <select
                                            required
                                            value={formData.region}
                                            onChange={handleFieldChange("region")}
                                        >
                                            <option value="">Seleccionar</option>
                                            {ARGENTINA_PROVINCES.map((province) => (
                                                <option key={province} value={province}>
                                                    {province}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            required
                                            value={formData.region}
                                            onChange={handleFieldChange("region")}
                                            placeholder=""
                                        />
                                    )}
                                </label>
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Localidad")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.city}
                                        onChange={handleFieldChange("city")}
                                        placeholder=""
                                    />
                                </label>
                            </div>

                            <div className="enrollment-field-grid single short">
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Código postal")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.postalCode}
                                        onChange={handleFieldChange("postalCode")}
                                        placeholder="CP"
                                    />
                                </label>
                            </div>

                            <div className="enrollment-field-grid address">
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Calle")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.street}
                                        onChange={handleFieldChange("street")}
                                        placeholder="Av. Cabildo"
                                    />
                                </label>
                                <label className="enrollment-field">
                                    <span>{renderRequiredLabel("Número")}</span>
                                    <input
                                        type="text"
                                        required
                                        value={formData.streetNumber}
                                        onChange={handleFieldChange("streetNumber")}
                                        placeholder="1234"
                                    />
                                </label>
                                <label className="enrollment-field">
                                    <span>Piso</span>
                                    <input
                                        type="text"
                                        value={formData.floor}
                                        onChange={handleFieldChange("floor")}
                                        placeholder=""
                                    />
                                </label>
                                <label className="enrollment-field">
                                    <span>Depto.</span>
                                    <input
                                        type="text"
                                        value={formData.apartment}
                                        onChange={handleFieldChange("apartment")}
                                        placeholder=""
                                    />
                                </label>
                            </div>

                            {isArgentinaForm ? (
                                <div className="enrollment-field-grid two">
                                    <label className="enrollment-field">
                                        <span>{renderRequiredLabel("Condición fiscal")}</span>
                                        <select
                                            required
                                            value={formData.fiscalCondition}
                                            onChange={handleFieldChange("fiscalCondition")}
                                        >
                                            {FISCAL_CONDITIONS.map((condition) => (
                                                <option
                                                    key={condition}
                                                    value={condition}
                                                >
                                                    {condition}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="enrollment-field">
                                        <span>{renderRequiredLabel("CUIT / CUIL")}</span>
                                        <input
                                            type="text"
                                            required
                                            value={formData.taxId}
                                            onChange={handleFieldChange("taxId")}
                                            placeholder="20-30123456-5"
                                        />
                                    </label>
                                </div>
                            ) : (
                                <div className="enrollment-field-grid two">
                                    <label className="enrollment-field">
                                        <span>{renderRequiredLabel("Tipo de documento")}</span>
                                        <select
                                            required
                                            disabled={!formData.country}
                                            value={formData.documentType}
                                            onChange={handleFieldChange("documentType")}
                                        >
                                            <option value="">
                                                {formData.country
                                                    ? "Seleccionar"
                                                    : "Seleccionar país primero"}
                                            </option>
                                            {documentTypeOptions.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="enrollment-field">
                                        <span>{renderRequiredLabel("Número de documento")}</span>
                                        <input
                                            type="text"
                                            required
                                            disabled={!formData.country}
                                            value={formData.documentNumber}
                                            onChange={handleFieldChange("documentNumber")}
                                            placeholder=""
                                        />
                                    </label>
                                </div>
                            )}

                            <div className="enrollment-terms">
                                <label className="enrollment-check">
                                    <input
                                        type="checkbox"
                                        required
                                        checked={formData.acceptTerms}
                                        onChange={handleFieldChange("acceptTerms")}
                                    />
                                    <span>
                                        Acepto los Términos y Condiciones y la
                                        Política de Privacidad de FISP.
                                    </span>
                                </label>
                                <label className="enrollment-check">
                                    <input
                                        type="checkbox"
                                        required
                                        checked={formData.acceptInvoice}
                                        onChange={handleFieldChange("acceptInvoice")}
                                    />
                                    <span>
                                        Declaro que los datos ingresados son correctos
                                        y autorizo la emisión de la factura
                                        correspondiente.
                                    </span>
                                </label>
                            </div>
                        </section>

                        <aside className="enrollment-sidebar">
                            {paymentSidebarLoading ? (
                                <EnrollmentPaymentLoadingCard />
                            ) : (
                            <div className="enrollment-summary-card">
                                <div className="enrollment-summary-hero">
                                    <span className="enrollment-summary-hero-label">Tu inscripción</span>
                                    <h2>{normalizedCourse.nombre}</h2>
                                    <p>Seleccioná tu método de pago</p>
                                </div>

                                <div className="enrollment-summary-body">
                                    <div className="enrollment-methods">
                                        {paymentOptions.map((option) => {
                                            const isActive =
                                                selectedMethodKey === option.key;
                                            const variantClass = option.kind.includes("mercado-pago")
                                                ? "is-mercado-pago"
                                                : option.kind.includes("paypal")
                                                ? "is-paypal"
                                                : option.kind === "transfer"
                                                ? "is-transfer"
                                                : "is-card";
                                            const displayTitle =
                                                option.kind ===
                                                "dlocalgo-subscription"
                                                    ? `${option.installmentsCount || curso?.cuotas} cuotas sin interés`
                                                    : option.kind.includes("paypal")
                                                    ? "PayPal"
                                                    : option.kind.includes(
                                                          "mercado-pago",
                                                      )
                                                    ? "Mercado Pago"
                                                : option.kind === "transfer" &&
                                                  isArgentinaPaymentFlow
                                                ? "Transferencia bancaria"
                                                : option.title;
                                            const displaySubtitle = option.kind === "dlocalgo-subscription" ? "Por suscripción" : option.kind.includes(
                                                "paypal",
                                            )
                                                ? option.kind === "paypal"
                                                    ? "1 pago"
                                                    : "Por suscripción"
                                                : option.kind.includes(
                                                "mercado-pago",
                                            )
                                                ? option.key === "mercado-pago-once"
                                                    ? "1 pago"
                                                    : "Por suscripción"
                                                : option.kind === "transfer"
                                                ? "1 pago"
                                                : option.subtitle;
                                            const formatMethodAmount = (amount) =>
                                                formatCurrencyWithOptionalCode(
                                                    amount,
                                                    option.currencyCode || "ARS",
                                                    option.countryCode || "AR",
                                                    option.showCurrencyCodeSuffix,
                                                );
                                            const highlightLabel = option.monthlyAmount
                                                ? `${option.installmentsCount || curso.cuotas} cuotas de`
                                                : option.savings
                                                ? "Total con descuento"
                                                : "1 pago de";
                                            const highlightValue =
                                                option.monthlyAmount
                                                    ? formatMethodAmount(
                                                          option.monthlyAmount,
                                                      )
                                                    : option.totalLabel;
                                            const badgeLabel = option.savings
                                                ? `${option.discountPercent || 20}% OFF`
                                                : null;
                                            const transferDetails =
                                                option.kind === "transfer"
                                                    ? option.transferDetails ||
                                                      ARGENTINA_TRANSFER_DETAILS
                                                    : [];

                                            const methodNote =
                                                option.kind === "dlocalgo-subscription"
                                                    ? `Se debita la primera cuota ahora y las siguientes quedan listas para cobro recurrente cada mes durante ${option.installmentsCount || curso.cuotas} meses.`
                                                    : option.kind ===
                                                "mercado-pago-subscription"
                                                    ? "Débito automático mensual por Mercado Pago. Se debita el mismo dí­a cada mes. Podés cancelar desde tu cuenta MP."
                                                    : option.kind ===
                                                        "mercado-pago"
                                                    ? "Acreditación inmediata al finalizar el pago."
                                                    : option.kind ===
                                                        "paypal-subscription"
                                                    ? null
                                                    : option.kind === "paypal" &&
                                                      option.savings
                                                    ? `Ahorrás ${formatMethodAmount(
                                                          option.savings,
                                                      )} pagando en 1 solo pago por PayPal.`
                                                    : option.kind ===
                                                          "transfer" &&
                                                        option.savings
                                                    ? `Ahorrás ${formatCurrency(
                                                          option.savings,
                                                          option.currencyCode ||
                                                              "ARS",
                                                          option.countryCode ||
                                                              "AR",
                                                      )} pagando en 1 solo pago por transferencia bancaria${option.currencyCode === "USD" ? " en USD" : ""}.`
                                                    : option.kind === "transfer" &&
                                                      !isArgentinaPaymentFlow
                                                    ? "Compartiremos los datos bancarios al confirmar la solicitud."
                                                    : null;

                                            const methodNoteDisplay =
                                                option.kind === "dlocalgo-subscription"
                                                    ? `Se debita la primera cuota ahora y las siguientes quedan listas para cobro recurrente cada mes durante ${option.installmentsCount || curso.cuotas} meses.`
                                                    : option.kind ===
                                                "mercado-pago-subscription"
                                                    ? "Débito automático mensual por Mercado Pago. Se debita el mismo día cada mes. Podés cancelar desde tu cuenta MP."
                                                    : option.kind ===
                                                        "mercado-pago"
                                                    ? "Acreditación inmediata al finalizar el pago."
                                                    : option.kind ===
                                                        "paypal-subscription"
                                                    ? null
                                                    : option.kind === "paypal" &&
                                                      option.savings
                                                    ? `Ahorrás ${formatMethodAmount(
                                                          option.savings,
                                                      )} pagando en 1 solo pago por PayPal.`
                                                    : option.kind ===
                                                          "transfer" &&
                                                        option.savings
                                                    ? `Ahorrás ${formatCurrency(
                                                          option.savings,
                                                          option.currencyCode ||
                                                              "ARS",
                                                          option.countryCode ||
                                                              "AR",
                                                      )} pagando en 1 solo pago por transferencia bancaria${option.currencyCode === "USD" ? " en USD" : ""}.`
                                                    : option.kind === "transfer" &&
                                                      !isArgentinaPaymentFlow
                                                    ? "Compartiremos los datos bancarios al confirmar la solicitud."
                                                    : null;

                                            return (
                                                <div
                                                    key={option.key}
                                                    className={`enrollment-method-wrapper${isActive && (option.kind === "card" || option.kind === "dlocalgo-subscription") ? " has-card-fields" : ""}`}
                                                >
                                                <label
                                                    className={`enrollment-method ${variantClass}${isActive ? " is-active" : ""}${isActive && (option.kind === "card" || option.kind === "dlocalgo-subscription") ? " is-card-open" : ""}`}
                                                    onClick={() => setSelectedMethodKey(option.key)}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="payment-method"
                                                        value={option.key}
                                                        checked={isActive}
                                                        onChange={() =>
                                                            setSelectedMethodKey(
                                                                option.key,
                                                            )
                                                        }
                                                    />

                                                    <div className={`enrollment-method-top ${variantClass}`}>
                                                        <div className="enrollment-method-head">
                                                            <span className="enrollment-method-radio"></span>
                                                            <div className="enrollment-method-title-wrap">
                                                                <span className="enrollment-method-title">
                                                                    {displayTitle}
                                                                </span>
                                                                <span
                                                                    className={`enrollment-method-subtitle${
                                                                        option.kind ===
                                                                            "paypal" &&
                                                                        badgeLabel
                                                                            ? " has-inline-badge"
                                                                            : ""
                                                                    }`}
                                                                >
                                                                    {displaySubtitle}
                                                                    {option.kind ===
                                                                        "paypal" &&
                                                                    badgeLabel ? (
                                                                        <span className="enrollment-method-inline-badge">
                                                                            {badgeLabel}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                            </div>
                                                            {option.kind.includes(
                                                                "mercado-pago",
                                                            ) ? (
                                                                <img
                                                                    src={mercadoPagoLogo}
                                                                    alt="Mercado Pago"
                                                                    className="enrollment-method-logo"
                                                                />
                                                            ) : option.kind.includes("paypal") ? (
                                                                <span className="enrollment-method-paypal-brand">
                                                                    <span
                                                                        className="enrollment-method-paypal-logo"
                                                                        aria-label="PayPal"
                                                                    >
                                                                        Pay<span>Pal</span>
                                                                    </span>
                                                                </span>
                                                            ) : option.kind ===
                                                              "dlocalgo-subscription" ? (
                                                                <div></div>
                                                            ) : badgeLabel ? (
                                                                <span className={`enrollment-method-badge ${option.savings ? "is-discount" : ""}`}>
                                                                    {badgeLabel}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>

                                                    <div className="enrollment-method-body">
                                                        {option.baseAmount &&
                                                        option.savings ? (
                                                            <div className="enrollment-method-meta is-reference">
                                                                <span>Valor regular</span>
                                                                <strong className="is-strike">
                                                                    {formatMethodAmount(
                                                                        option.baseAmount,
                                                                    )}
                                                                </strong>
                                                            </div>
                                                        ) : null}

                                                        {option.savings &&
                                                        option.kind !==
                                                            "transfer" ? (
                                                            <div className="enrollment-method-meta is-saving">
                                                                <span>Descuento aplicado</span>
                                                                <strong>
                                                                    {formatMethodAmount(
                                                                        option.savings,
                                                                    )}
                                                                </strong>
                                                            </div>
                                                        ) : null}

                                                        {option.monthlyAmount ? (
                                                            <div className="enrollment-method-meta">
                                                                <span>Total</span>
                                                                <strong>
                                                                    {option.totalLabel}
                                                                </strong>
                                                            </div>
                                                        ) : null}

                                                        <div
                                                            className={`enrollment-method-highlight ${variantClass}`}
                                                        >
                                                            <span className="enrollment-method-highlight-label">
                                                                {highlightLabel}
                                                            </span>
                                                            <strong className="enrollment-method-highlight-value">
                                                                {highlightValue}
                                                                {!option.monthlyAmount &&
                                                                !option.showCurrencyCodeSuffix &&
                                                                option.currencyCode &&
                                                                shouldAppendCurrencyCode(
                                                                    option.currencyCode,
                                                                    option.countryCode || "AR",
                                                                ) ? (
                                                                    <small className="enrollment-method-highlight-currency">
                                                                        {" "}{option.currencyCode}
                                                                    </small>
                                                                ) : null}
                                                                {option.monthlyAmount ? (
                                                                    <small className="enrollment-method-highlight-suffix">
                                                                        /mes
                                                                    </small>
                                                                ) : null}
                                                            </strong>
                                                        </div>

                                                        {/* CAMBIO: solo renderizar nota si hay contenido real */}
                                                        {(methodNoteDisplay || methodNote) ? (
                                                            <p
                                                                className={`enrollment-method-note${
                                                                    option.kind ===
                                                                        "transfer" &&
                                                                    option.savings
                                                                        ? " is-saving"
                                                                        : ""
                                                                }`}
                                                            >
                                                                {methodNoteDisplay ||
                                                                    methodNote}
                                                            </p>
                                                        ) : null}

                                                        {isActive &&
                                                        Boolean(option.savings) &&
                                                        ((option.kind === "transfer" &&
                                                            isArgentinaPaymentFlow) ||
                                                            (option.kind === "card" &&
                                                                !isArgentinaPaymentFlow)) ? (
                                                            <div className="enrollment-discount-code-box">
                                                                <div className="enrollment-field enrollment-discount-code-field">
                                                                    <span>Código de descuento</span>
                                                                    <input
                                                                        type="text"
                                                                        value={singlePaymentDiscountCode}
                                                                        onChange={handleDiscountCodeChange}
                                                                        placeholder="Ingresá tu código"
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                                {option.discountCodeApplied ? (
                                                                    <p className="enrollment-method-note is-saving enrollment-discount-code-feedback">
                                                                        Código aplicado. El descuento en 1 pago pasó de 20% a 30%.
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        ) : null}

                                                        {isActive &&
                                                        transferDetails.length ? (
                                                            <div className="enrollment-method-bank">
                                                                <span className="enrollment-method-bank-title">
                                                                    Datos bancarios
                                                                </span>
                                                                {transferDetails.map(
                                                                    (detail) => (
                                                                        <div
                                                                            className="enrollment-method-bank-row"
                                                                            key={detail.label}
                                                                        >
                                                                            <span>
                                                                                {detail.label}
                                                                            </span>
                                                                            <strong
                                                                                className={
                                                                                    detail.mono
                                                                                        ? "is-mono"
                                                                                        : ""
                                                                                }
                                                                            >
                                                                                {detail.value}
                                                                            </strong>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        ) : null}
                                                    </div>

                                                </label>

                                                    {/* Formulario de tarjeta fuera del label pero dentro del wrapper */}
                                                    {isActive && (option.kind === "card" || option.kind === "dlocalgo-subscription") ? (
                                                        <div className="enrollment-card-fields">
                                                            <p className="enrollment-card-fields-intro">
                                                                Ingresá los datos de tu tarjeta:
                                                            </p>



                                                            <div className="enrollment-card-grid">
                                                                <label className="enrollment-field enrollment-card-grid-full">
                                                                    <span>{renderRequiredLabel("Titular")}</span>
                                                                    <input
                                                                        type="text"
                                                                        required={needsEmbeddedCard}
                                                                        value={formData.cardHolder}
                                                                        onChange={handleFieldChange("cardHolder")}
                                                                        placeholder="Nombre como figura en la tarjeta"
                                                                    />
                                                                </label>
                                                                <div className="enrollment-field enrollment-card-grid-full">
                                                                    <span>{renderRequiredLabel("Número de tarjeta")}</span>
                                                                    <div
                                                                        ref={cardContainerRef}
                                                                        className="enrollment-card-smart-field"
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                            {exchangeLoading ? (
                                                                <p className="enrollment-card-status">
                                                                    Estamos calculando el precio en tu moneda local...
                                                                </p>
                                                            ) : null}
                                                            {!isArgentinaPaymentFlow && !exchangeLoading ? (
                                                                <p className="enrollment-card-status"></p>
                                                            ) : null}
                                                            {exchangeError ? (
                                                                <p className="enrollment-card-status is-muted">
                                                                    {exchangeError}
                                                                </p>
                                                            ) : null}
                                                            {dlocalLoading ? (
                                                                <p className="enrollment-card-status">
                                                                    {option.kind === "dlocalgo-subscription"
                                                                        ? "Preparando el formulario seguro para la suscripcion recurrente."
                                                                        : "Cargando formulario seguro de tarjeta."}
                                                                </p>
                                                            ) : null}
                                                            {paymentError ? (
                                                                <p className="enrollment-error">
                                                                    {paymentError}
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="enrollment-summary">
                                        {summaryBaseAmount ? (
                                            <div className="enrollment-summary-line">
                                                <span>Arancel del programa</span>
                                                <strong>{summaryBaseAmount}</strong>
                                            </div>
                                        ) : null}
                                        {summaryDiscount ? (
                                            <div className="enrollment-summary-line is-saving">
                                                <span>
                                                    {selectedMethod?.discountCodeApplied &&
                                                    selectedMethod?.discountPercent
                                                        ? `Descuento aplicado (${selectedMethod.discountPercent}% OFF)`
                                                        : "Descuento aplicado"}
                                                </span>
                                                <strong>−{summaryDiscount}</strong>
                                            </div>
                                        ) : null}
                                        {/* CAMBIO: muestra moneda + nombre del país en gris */}
                                        {!isArgentinaPaymentFlow &&
                                        selectedMethod?.currencyCode &&
                                        !selectedMethod?.showCurrencyCodeSuffix ? (
                                            <div className="enrollment-summary-line">
                                                <span>Moneda de pago</span>
                                                <strong>
                                                    {selectedMethod.currencyCode}{" "}
                                                    {selectedMethod.currencyCode !== "USD" && (
                                                        <small className="enrollment-summary-country-hint">
                                                            ({COUNTRY_OPTIONS.find(
                                                                (c) => c.code === selectedMethod.countryCode
                                                            )?.label || selectedMethod.countryCode})
                                                        </small>
                                                    )}
                                                </strong>
                                            </div>
                                        ) : null}
                                        <div className="enrollment-summary-total">
                                            <span>Total a pagar</span>
                                            <div>
                                                <strong>
                                                    {selectedMethod?.totalLabel ||
                                                        "A definir"}
                                                </strong>
                                                <small>
                                                    {selectedMethod?.summaryNote ||
                                                        "Según medio elegido"}
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="enrollment-submit"
                                        disabled={
                                            !selectedMethod ||
                                            cardSubmitting ||
                                            (needsEmbeddedCard &&
                                                (exchangeLoading ||
                                                    dlocalLoading ||
                                                    !sdkReady))
                                        }
                                    >
                                        {selectedMethod?.kind === "card"
                                            ? cardSubmitting
                                                ? "Procesando tarjeta..."
                                                : dlocalLoading
                                                ? "Preparando pago..."
                                                : "Continuar con tarjeta"
                                            : selectedMethod?.kind ===
                                              "transfer"
                                            ? "Confirmar inscripción"
                                            : selectedMethod?.kind ===
                                              "dlocalgo-subscription"
                                            ? cardSubmitting
                                                ? "Activando suscripcion..."
                                                : dlocalLoading
                                                ? "Preparando suscripcion..."
                                                : "Activar suscripcion"
                                            : selectedMethod?.kind === "paypal"
                                            ? "Continuar"
                                            : selectedMethod?.kind ===
                                              "paypal-subscription"
                                            ? "Continuar"
                                            : "Continuar con Mercado Pago"}
                                    </button>
                                </div>
                            </div>
                            )}
                        </aside>
                    </form>
                </div>
            </main>

            <SiteFooter />
        </div>
    );
}
